/**
 * Session-resolver helper consumed by route guards.
 *
 * The `meQueryOptions` pair is stable so route `beforeLoad` hooks can call
 * `queryClient.ensureQueryData(meQueryOptions)` and React components can
 * read the same cache entry via `useQuery`. The single source of truth for
 * "is the visitor signed in?" lives behind one wire call to `identity.me`.
 *
 * `Me` is derived directly from the Zod output schema in `@flint/api-contract`
 * — the placeholder `AppRouter` in `router-types.ts` doesn't carry inference
 * data yet (see its module docstring), so we lean on the schema instead. The
 * wire payload is null when unauthenticated, populated otherwise; `MeCompany`
 * is null between OTP verification and workspace creation.
 */
import type { z } from "zod";
import { identity } from "@flint/api-contract";
import { vanillaTrpc } from "./trpc.ts";

export type Me = z.infer<typeof identity.meOutput>;
export type MeUser = NonNullable<Me>["user"];
export type MeCompany = NonNullable<Me>["company"];

export const meQueryKey = ["identity", "me"] as const;

export const meQueryOptions = {
  queryKey: meQueryKey,
  queryFn: () => vanillaTrpc.identity.me.query({}) as Promise<Me>,
  // The session resolver is the one query route guards block on. Keeping it
  // fresh for a short window means in-app navigations don't refetch on every
  // hop, but a hard reload always re-resolves before the SPA paints.
  staleTime: 60_000,
} as const;
